import { Injectable } from '@angular/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, take, first, catchError } from 'rxjs/operators';
import { addDays, subDays, format as formatDate, startOfMonth, endOfMonth } from 'date-fns';
import { Store } from '@ngrx/store';
import gql from 'graphql-tag';
import {
  EvChRTV2,
  AHEvChRT,
  EvChRTData,
  EvChDataV2,
  Range,
  Series,
  getTimeApplyOffset,
  groupBy,
  GraphqlApiService,
  MonitoringType,
  LoggerEnumType,
} from 'ngx-core-services';

import {
  InteractiveSessions,
  initializeSessions,
  hasIncompleteSessions
} from '../evcharge-telemetry/evcharge-session.utils';
import { SystemsState, selectedSystemMetadata } from '../../store';

const MAX_DEEP_SESSION_SEARCH = 10;

@Injectable({
  providedIn: 'root'
})
export class EvchargeTelemetryService {

  constructor(
    private readonly graphqlApiService: GraphqlApiService,
    private readonly store: Store<SystemsState>
  ) { }

  /**
   * Process and clean telemetry data - same logic for both Connext and AzzurroHub
   */
  private processAndCleanData(data: EvChRTData[]): EvChRTData[] {
    let cleanedData = [...data];

    // Standard filters
    cleanedData = cleanedData.filter((d) => d.SEM !== d.EM);
    cleanedData = cleanedData.filter((d) => (d.SEM > 0 && d.CT === 0) === false);

    const byTIDA = groupBy(cleanedData, (value) => value.TID);
    for (const [TID, values] of Object.entries(byTIDA)) {
      const isValidTID = values.some((v) => v.SEM !== 0);
      cleanedData = cleanedData.filter((d) => (d.TID === TID && !isValidTID) === false);
    }

    return cleanedData;
  }

  /**
   * Process and clean aggregation data - filters invalid sessions from TIDA arrays
   * Uses the same filtering logic as processAndCleanData to ensure consistency
   */
  private processAndCleanAggregationData(data: EvChDataV2[]): EvChDataV2[] {
    console.log(`🔄 Cleaning aggregation data: ${data.length} days`);

    const cleaned = data
      .map((dayData) => {
        const originalSessionCount = dayData.TIDA?.length || 0;

        // Clean TIDA array using same logic as processAndCleanData
        let cleanedTIDA = dayData.TIDA?.filter((session) => {
          // Filter 1: Remove sessions where SEM equals EM (invalid session energy)
          if (session.SEM === (session as any).EM) return false;

          // Filter 2: Remove sessions with energy but no charge time
          if (session.SEM > 0 && session.CT === 0) return false;

          return true;
        }) || [];

        // Group sessions by TID and validate (same as processAndCleanData)
        const byTID = groupBy(cleanedTIDA, (value) => value.TID);
        const validTIDs = new Set<string>();

        for (const [TID, sessions] of Object.entries(byTID)) {
          const isValidTID = sessions.some((s) => s.SEM !== 0);
          if (isValidTID) {
            validTIDs.add(TID);
          }
        }

        // Filter sessions to keep only valid TIDs
        cleanedTIDA = cleanedTIDA.filter((session) => validTIDs.has(session.TID));

        // Recalculate aggregated values based on cleaned sessions
        const recalculatedCC = cleanedTIDA.length;
        const recalculatedDEM = cleanedTIDA.reduce((sum, session) => sum + (session.SEM || 0), 0);

        if (originalSessionCount !== cleanedTIDA.length) {
          console.log(`📊 Day ${dayData.SK}: ${originalSessionCount} → ${cleanedTIDA.length} sessions (DEM: ${dayData.DEM} → ${recalculatedDEM})`);
        }

        return {
          ...dayData,
          TIDA: cleanedTIDA,
          CC: recalculatedCC,
          DEM: recalculatedDEM
        };
      })
      .filter((dayData) => {
        // Remove days with no valid sessions
        return dayData.TIDA && dayData.TIDA.length > 0;
      });

    const removedDays = data.length - cleaned.length;
    if (removedDays > 0) {
      console.log(`✅ Cleaned aggregation data: ${cleaned.length} days (removed ${removedDays} empty days)`);
    } else {
      console.log(`✅ Cleaned aggregation data: ${cleaned.length} days`);
    }

    return cleaned;
  }

  /**
   * Smart caching logic: disable cache for current day to ensure real-time accuracy
   */
  private shouldUseCache(date: string, tz: string): boolean {
    const requestDate = new Date(date);
    const today = new Date();

    // Convert to same timezone for comparison
    const requestDateTz = getTimeApplyOffset(requestDate.getTime() / 1000, `-${tz}`);
    const todayTz = getTimeApplyOffset(today.getTime() / 1000, `-${tz}`);

    // Reset time to compare only dates
    requestDateTz.setHours(0, 0, 0, 0);
    todayTz.setHours(0, 0, 0, 0);

    // If request is for today, don't use cache
    return requestDateTz.getTime() !== todayTz.getTime();
  }

  /**
   * Get EVCharge telemetry data - single implementation for both dataloggers
   */
  getEvChargeTelemetryData(
    systemId: string,
    deviceId: string,
    dateFrom: string,
    dateTo: string,
    type: LoggerEnumType,
    loggerId?: string
  ): Observable<EvChRTV2[] | AHEvChRT[]> {
    return this.getSystemTimezone(systemId).pipe(
      switchMap(tz => {
        if (dateFrom === dateTo) {
          return this.getEvchargeDailyTelemetry(deviceId.toUpperCase(), dateFrom, tz, false, type, loggerId);
        }
        return this.getEvchargeRangeTelemetry(deviceId.toUpperCase(), { from: dateFrom, to: dateTo }, tz, type, loggerId);
      })
    );
  }

  /**
   * Get daily telemetry data with processing
   */
  private getEvchargeDailyTelemetry(
    serial: string,
    date: string,
    tz: string,
    refresh = false,
    type: LoggerEnumType,
    loggerId?: string
  ): Observable<EvChRTV2[] | AHEvChRT[]> {
    return this.getEvchargeDailyData(serial, date, tz, refresh, type, loggerId).pipe(
      map(dailyData => this.processAndCleanData(dailyData) as EvChRTV2[] | AHEvChRT[]),
      switchMap(async (dailyData) => {
        const sessions = initializeSessions(dailyData as any);
        return await this.deepInteraction(sessions, dailyData as any, serial, date, tz, refresh, type, loggerId);
      })
    );
  }

  /**
   * Get range telemetry data (multi-day)
   */
  private getEvchargeRangeTelemetry(
    serial: string,
    range: Range,
    tz: string,
    type: LoggerEnumType,
    loggerId?: string
  ): Observable<EvChRTV2[] | AHEvChRT[]> {
    // Generate array of dates within the range
    const dates = this.generateDateRange(range.from, range.to);

    // Create observables for each day
    const dailyObservables = dates.map(date =>
      this.getEvchargeDailyTelemetry(serial, date, tz, false, type, loggerId).pipe(
        catchError(() => of([]))
      )
    );

    // Combine all daily results
    return combineLatest(dailyObservables).pipe(
      map(dailyResults => {
        // Flatten all daily data into single array
        const combinedData = (dailyResults as any).flat();

        // Sort by timestamp to maintain chronological order
        return (combinedData as any).sort((a: any, b: any) => a.TS - b.TS);
      })
    );
  }

  /**
   * Get raw daily data using GraphQL queries
   */
  private getEvchargeDailyData(
    serial: string,
    date: string,
    tz: string,
    refresh = false,
    type: LoggerEnumType,
    loggerId?: string
  ): Observable<EvChRTData[]> {
    const useCache = refresh ? false : this.shouldUseCache(date, tz);
    const fetchPolicy = useCache ? 'cache-first' : 'network-only';

    // Calculate date range following the original service logic
    const _from = getTimeApplyOffset((new Date(date)).getTime() / 1000, `-${tz}`);
    const _to = getTimeApplyOffset((new Date(date)).getTime() / 1000, `-${tz}`);
    _to.setHours(_to.getHours() + 24);
    const range = { from: _from.toISOString(), to: _to.toISOString() };

    // Complete query for both Connext and AzzurroHub
    const query = gql`
      query GetEvChDataByRangeV2($serial: String!, $range: Range!, $type: LoggerEnumType, $loggerId: String) {
        getEvChDataByRangeV2(serial: $serial, range: $range, type: $type, loggerId: $loggerId) {
          ... on EvChRTV2 {
            ST
            TID
            AE
            SOC
            SK
            SEM
            RT
            PK
            PP
            ID
            GwId
            EM
            CT
            CP
            pcv
            created
            TS
          }
          ... on AHEvChRT {
            ST
            TID
            CP
            CT
            EM
            GwId
            pcv
            evchPwrImportLimit
            evchPwrImportValue
            evchParallelEna
            evchBatterySOCLimit
            evchBatteryProtectEna
            evchStrategy
            deviceType
            created
            TZ
            TS
            SK
            SEM
            PK
          }
        }
      }
    `;

    return this.graphqlApiService.client$.pipe(
      first(),
      switchMap(async client => {
        if (!client) {
          throw new Error('GraphQL client not available');
        }
        const result = await client.query<{ getEvChDataByRangeV2: EvChRTData[] }>({
          query,
          variables: { serial: serial.toUpperCase(), range, type, loggerId },
          fetchPolicy
        });
        return result.data.getEvChDataByRangeV2 || [];
      })
    );
  }


  /**
   * DeepInteraction - maintains type safety for Connext or AzzurroHub
   */
  private async deepInteraction(
    sessions: InteractiveSessions,
    dailyData: EvChRTV2[] | AHEvChRT[],
    serial: string,
    date: string,
    tz: string,
    refresh = false,
    type: LoggerEnumType,
    loggerId?: string
  ): Promise<EvChRTV2[] | AHEvChRT[]> {
    // Algorithm is identical for both types - uses only TID, TS which are common
    for (const session of sessions) {
      const sessionIsNotCompleted = !session.isFinish;
      const missingInteractions = session.deep < MAX_DEEP_SESSION_SEARCH;

      if (sessionIsNotCompleted && missingInteractions) {
        session.deep += 1;
        const from = getTimeApplyOffset((new Date(date)).getTime() / 1000, `-${tz}`);

        if (session.isStartToday === undefined) {
          const preDate = subDays(from, session.deep);
          const preDateFormatted = formatDate(preDate, 'yyyy-MM-dd');

          try {
            const preData = await this.getEvchargeDailyDataAsync(serial, preDateFormatted, tz, refresh, type, loggerId);
            // Type-safe check using common TID property
            session.isStartToday = !(preData as any).find((item: any) => item.TID === session.id);

            if (session.isStartToday === false) {
              session.isFinish = true;
              // Type-safe filter using common TID property
              dailyData = (dailyData as any).filter((item: any) => item.TID !== session.id);
              continue;
            }
          } catch {
            session.isFinish = true;
            continue;
          }
        }

        const postDate = addDays(from, session.deep);
        const postDateFormatted = formatDate(postDate, 'yyyy-MM-dd');

        try {
          const postData = await this.getEvchargeDailyDataAsync(serial, postDateFormatted, tz, refresh, type, loggerId);
          // Type-safe filter using common TID property
          const sessionPostData = (postData as any).filter((item: any) => item.TID === session.id);

          // Type-safe concatenation
          dailyData = (dailyData as any).concat(sessionPostData);
          // Type-safe find using common TID property
          session.isFinish = !(postData as any).find((item: any) => item.TID === session.id);
        } catch {
          session.isFinish = true;
        }
      }
    }

    return hasIncompleteSessions(sessions)
      ? await this.deepInteraction(sessions, dailyData, serial, date, tz, refresh, type, loggerId)
      : dailyData;
  }

  /**
   * Async wrapper for daily data - maintains type consistency
   */
  private async getEvchargeDailyDataAsync(
    serial: string,
    date: string,
    tz: string,
    refresh = false,
    type: LoggerEnumType,
    loggerId?: string
  ): Promise<EvChRTV2[] | AHEvChRT[]> {
    try {
      const result = await this.getEvchargeDailyData(serial, date, tz, refresh, type, loggerId)
        .pipe(
          map(data => this.processAndCleanData(data) as EvChRTV2[] | AHEvChRT[]),
          take(1)
        )
        .toPromise();
      return (result || []) as EvChRTV2[] | AHEvChRT[];
    } catch {
      return [];
    }
  }

  /**
   * Get system timezone from Redux state metadata
   */
  private getSystemTimezone(_systemId: string): Observable<string> {
    return this.store.select(selectedSystemMetadata).pipe(
      first(),
      map(metadata => {
        if (metadata?.tz !== undefined) {
          // Convert tz to the format expected by getTimeApplyOffset
          // metadata.tz is already divided by 4, multiply back to get original value
          return String(metadata.tz * 4);
        }
        return '2'; // Default timezone
      })
    );
  }

  /**
   * Generate array of date strings within a range (YYYY-MM-DD format)
   */
  private generateDateRange(fromDate: string, toDate: string): string[] {
    const dates: string[] = [];
    const start = new Date(fromDate);
    const end = new Date(toDate);

    // Reset time to midnight for consistent date comparison
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const current = new Date(start);
    while (current <= end) {
      dates.push(formatDate(current, 'yyyy-MM-dd'));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  /**
   * Get aggregated energy data for a year
   */
  getEvChargeYearlyAggregation(
    systemId: string,
    deviceId: string,
    date: Date,
    type: LoggerEnumType,
    loggerId?: string
  ): Observable<EvChDataV2[]> {
    const yearStart = new Date(date.getFullYear(), 0, 1);
    const yearEnd = new Date(date.getFullYear(), 11, 31);

    const range: Range = {
      from: formatDate(yearStart, 'yyyy-MM-dd'),
      to: formatDate(yearEnd, 'yyyy-MM-dd')
    };

    return this.getSystemTimezone(systemId).pipe(
      switchMap(tz => this.getEvChargeAggregation(deviceId, range, tz, type, loggerId))
    );
  }

  /**
   * Get aggregated energy data for a month
   */
  getEvChargeMonthlyAggregation(
    systemId: string,
    deviceId: string,
    date: Date,
    type: LoggerEnumType,
    loggerId?: string
  ): Observable<EvChDataV2[]> {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);

    const range: Range = {
      from: formatDate(monthStart, 'yyyy-MM-dd'),
      to: formatDate(monthEnd, 'yyyy-MM-dd')
    };

    return this.getSystemTimezone(systemId).pipe(
      switchMap(tz => this.getEvChargeAggregation(deviceId, range, tz, type, loggerId))
    );
  }

  /**
   * Get aggregated energy data using GetEvChDataByAggregationV2Query
   */
  private getEvChargeAggregation(
    serial: string,
    range: Range,
    tz: string,
    type: LoggerEnumType,
    loggerId?: string
  ): Observable<EvChDataV2[]> {
    const useCache = this.shouldUseCache(range.to, tz);
    const fetchPolicy = useCache ? 'cache-first' : 'network-only';

    const query = gql`
      query GetEvChDataByAggregationV2($serial: String!, $range: Range!, $series: Series!, $type: LoggerEnumType, $loggerId: String) {
        getEvChDataByAggregationV2(serial: $serial, range: $range, series: $series, type: $type, loggerId: $loggerId) {
          ... on EvChDAYV2 {
            created
            PK
            TS
            SK
            GwId
            EM
            DEM
            DCT
            CC
            TIDA {
              TS
              TID
              SEM
              PP
              CT
              __typename
            }
            __typename
          }
          ... on AHEvChDAY {
            created
            PK
            deviceType
            TS
            SK
            GwId
            EM
            DEM
            DCT
            CC
            TIDA {
              TS
              TID
              SEM
              PP
              CT
              __typename
            }
            __typename
          }
        }
      }
    `;

    return this.graphqlApiService.client$.pipe(
      first(),
      switchMap(async client => {
        if (!client) {
          throw new Error('GraphQL client not available');
        }
        const result = await client.query<{ getEvChDataByAggregationV2: EvChDataV2[] }>({
          query,
          variables: {
            serial: serial.toUpperCase(),
            range,
            series: Series.DAY,
            type,
            loggerId
          },
          fetchPolicy
        });

        const rawData = result.data.getEvChDataByAggregationV2 || [];

        // Apply data cleaning to ensure consistency with daily telemetry
        return this.processAndCleanAggregationData(rawData);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Multi-device aggregated energy data (DAY series)
   * Returns a map { [deviceId]: EvChDataV2[] }
   */
  async fetchEvchargeAggregation(
    deviceIds: string[],
    range: Range,
    tz: string,
    type?: LoggerEnumType,
    loggerId?: string
  ): Promise<{ [key: string]: EvChDataV2[] }> {
    const useCache = this.shouldUseCache(range.to, tz);
    const fetchPolicy = useCache ? 'cache-first' : 'network-only';

    const query = gql`${this.BuildEvChAggregationQuery(deviceIds.map(id => ({ id })), range, type, loggerId)}`;

    const client = await this.graphqlApiService.client$.pipe(first()).toPromise();
    if (!client) throw new Error('GraphQL client not available');

    const result = await client.query<{ [key: string]: EvChDataV2[] }>({
      query,
      fetchPolicy,
    });

    const data = result.data || {} as { [key: string]: EvChDataV2[] };

    // Clean per-device arrays to keep consistency with daily telemetry cleaning rules
    const cleaned: { [key: string]: EvChDataV2[] } = {};
    for (const key of Object.keys(data)) {
      const arr = (data as any)[key] as EvChDataV2[];
      cleaned[key] = this.processAndCleanAggregationData(arr);
    }

    return cleaned;
  }

  /**
   * Build multi-device aggregation query for getEvChDataByAggregationV2
   * Uses device.id as both alias and serial string
   */
  private BuildEvChAggregationQuery = (
    devices: { id: string }[],
    range: Range,
    type?: LoggerEnumType,
    loggerId?: string
  ): string => {
    const rangeStr = JSON.stringify(range).replace(/"(\w+)":/g, '$1:');

    const deviceQueries = devices.map((device) => {
      const serial = device.id; // alias == serial as requested

      const args: string[] = [
        `serial: "${serial}"`,
        `range: ${rangeStr}`,
        `series: DAY`, // GraphQL enum literal
      ];
      if (typeof type !== 'undefined') args.push(`type: ${type}`);
      if (loggerId) args.push(`loggerId: "${loggerId}"`);

      return `
        ${serial}: getEvChDataByAggregationV2(${args.join(', ')}) {
          ... on EvChDAYV2 {
            created
            PK
            TS
            SK
            GwId
            EM
            DEM
            DCT
            CC
            TIDA {
              TS
              TID
              SEM
              PP
              CT
              __typename
            }
            __typename
          }
          ... on AHEvChDAY {
            created
            PK
            deviceType
            TS
            SK
            GwId
            EM
            DEM
            DCT
            CC
            TIDA {
              TS
              TID
              SEM
              PP
              CT
              __typename
            }
            __typename
          }
        }
      `;
    });

    return `query { ${deviceQueries.join('')} }`;
  };

  async fetchEvchargeDailyData(
  deviceIds: string[],
  date: string,
  tz: string,
  refresh = false,
  type?: LoggerEnumType,
  loggerId?: string
  ): Promise<{ [key: string]: EvChRTData[] }> {
    const baseTs = (new Date(date)).getTime() / 1000;
    const _from = getTimeApplyOffset(baseTs, `-${tz}`);
    const _to = getTimeApplyOffset(baseTs, `-${tz}`);
    _to.setHours(_to.getHours() + 24);
    const range: Range = { from: _from.toISOString(), to: _to.toISOString() };

    const query = gql`${this.BuildEvChDataByRangeQuery(deviceIds.map(id => ({ id })), range, type, loggerId)}`;

    const client = await this.graphqlApiService.client$.pipe(first()).toPromise();
    if (!client) throw new Error('GraphQL client not available');

    const fetchPolicy = refresh ? 'network-only' : 'cache-first';

    const result = await client.query<{ [key: string]: EvChRTData[] }>({
      query,
      fetchPolicy,
    });

    return result.data;
  }

private BuildEvChDataByRangeQuery = (
  devices: { id: string }[],
  range: Range,
  type?: LoggerEnumType,
  loggerId?: string
  ): string => {
    const rangeStr = JSON.stringify(range).replace(/"(\w+)":/g, '$1:');

    const deviceQueries = devices.map((device) => {
      const serial = device.id;

      const args: string[] = [
        `serial: "${serial}"`,
        `range: ${rangeStr}`,
      ];
      if (typeof type !== 'undefined') args.push(`type: ${type}`);
      if (loggerId) args.push(`loggerId: "${loggerId}"`);

      return `
        ${serial}: getEvChDataByRangeV2(${args.join(', ')}) {
          ... on EvChRTV2 {
            ST
            TID
            AE
            SOC
            SK
            SEM
            RT
            PK
            PP
            ID
            GwId
            EM
            CT
            CP
            pcv
            created
            TS
          }
          ... on AHEvChRT {
            ST
            TID
            CP
            CT
            EM
            GwId
            pcv
            evchPwrImportLimit
            evchPwrImportValue
            evchParallelEna
            evchBatterySOCLimit
            evchBatteryProtectEna
            evchStrategy
            deviceType
            created
            TZ
            TS
            SK
            SEM
            PK
          }
        }
      `;
    });

    return `query { ${deviceQueries.join('')} }`;
  };

}
